import { Grid, Tooltip } from "@mui/material";
import { FC } from "react";
import { api } from "@/helper/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import styles from "@/components/appointmentDashboard/appointmentTreeItemActionButtons.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import classNames from "classnames";

interface Props {
  appointment: any;
  appointmentId: string;
  handleSetError: (error: any) => void;
  handleSetCurrentAppointment: (appointment: any) => void;
  allButtonsDisabled?: boolean;
  executedById?: string | number;
  className?: string;
}

const AppointmentTreeItemActionButtons: FC<Props> = ({
  appointment,
  appointmentId,
  handleSetError,
  handleSetCurrentAppointment,
  allButtonsDisabled,
  executedById,
  className,
}) => {
  const { t } = useTranslation();
  const { showConfirmationModalAsync, showConfirmationModal } =
    useConfirmationModal();

  const loggedInSubjectId = useSelector(
    (state: RootState) => state.login.subjectId,
  );
  const isNotResponsibleUser =
    Boolean(executedById) &&
    String(executedById) !== String(loggedInSubjectId);
  const statusButtonsDisabled = allButtonsDisabled || isNotResponsibleUser;

  const withResponsibleUserTooltip = (button: JSX.Element, key: string) =>
    isNotResponsibleUser ? (
      <Tooltip
        key={key}
        title={t("appointment_status_change_only_responsible_person")}
        placement="top"
      >
        <span className={styles.tooltipWrapper}>{button}</span>
      </Tooltip>
    ) : (
      button
    );

  const handleError = (error: any) => {
    handleSetError(error);
  };
  const navigate = useNavigate();
  let appointmentActionsButtons: any = <></>;
  const loadAppointment = async (
    appointmentId: string,
    status?: string,
    onSuccessHandler?: (data: any) => void,
  ) => {
    if (!appointmentId) return;
    try {
      if (
        status === "review_preparation_required" ||
        status === "post_processing_required"
      ) {
        const waitForTasksToComplete = async (appointmentId: string) => {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const response: any = await new Promise((resolve, reject) => {
              api.genericApiRequest({
                entity: "appointment",
                method: "get",
                entityId: appointmentId,
                additionalRouteParts: { wait_for_all_tasks_to_complete: "/" },
                successHandler: (res: any) => resolve(res),
                failHandler: (error: any) => reject(error),
              });
            });
            const status = response?.data?.status;

            if (status === "completed") {
              return status;
            }

            await new Promise((r) => setTimeout(r, 5000));
          }
        };
        const result = await waitForTasksToComplete(appointmentId);
        if (result === "completed") {
          await getAppointment(appointmentId, onSuccessHandler);
        }
      } else {
        await getAppointment(appointmentId, onSuccessHandler);
      }
    } catch (error) {
      handleSetError(error);
      throw error;
    }
  };

  const getAppointment = (appointmentId: any, onSuccessHandler: any) => {
    const additionalRoutePartsOnSuccessHandlerCall: Record<string, string> = {
      dashboard: "/",
    };
    api.genericApiRequest({
      entity: "appointment",
      method: "get",
      entityId: appointmentId,
      additionalRouteParts: additionalRoutePartsOnSuccessHandlerCall,
      successHandler: (res: any) => {
        handleSetCurrentAppointment(res.data);

        if (onSuccessHandler) {
          onSuccessHandler(res.data);
        }
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const renderAppointmentActionButtons = () => {
    const buttons = [];
    const additionalRouteParts: any = {};
    additionalRouteParts["set_status"] = "/";

    const inProgressButton = (
      <GenericButton
        key={appointment.id + "in_progress"}
        data-testid="set-in-progress-button"
        disabled={allButtonsDisabled}
        color="tertiary"
        className={styles.button}
        onClick={async () => {
          const requestData = {
            status: "in_progress",
          };
          const isConfirmed = await showConfirmationModalAsync(
            t("appointment_confirm_status_in_progress"),
          );
          if (!isConfirmed) return;

          api.genericApiRequest({
            entity: "appointment",
            method: "put",
            successHandler: async () => {
              await loadAppointment(appointmentId);
            },
            failHandler: (error: any) => {
              handleSetError(error);
            },
            entityId: appointment.id,
            additionalRouteParts: additionalRouteParts,
            submitData: requestData,
          });
        }}
      >
        {t("status_set_in_progress")}
      </GenericButton>
    );

    const waitingForLabResultsButton = (
      <GenericButton
        key={appointment.id + "waiting_for_lab_results"}
        disabled={allButtonsDisabled}
        className={styles.waitingForResultButton}
        data-testid="waiting-for-lab-results-button"
        onClick={async () => {
          const requestData = {
            status: "waiting_for_lab_results",
          };
          const isConfirmed = await showConfirmationModalAsync(
            t("appointment_confirm_status_change"),
          );

          if (!isConfirmed) return;

          api.genericApiRequest({
            entity: "appointment",
            method: "put",
            successHandler: async () => {
              await loadAppointment(appointmentId, "waiting_for_lab_results");
            },
            failHandler: (error: any) => {
              handleSetError(error);
            },
            entityId: appointment.id,
            additionalRouteParts: additionalRouteParts,
            parametersToRender: {
              depth: "0",
            },
            submitData: requestData,
          });
        }}
      >
        {t("status_waiting_for_lab_results")}
      </GenericButton>
    );
    const evaluatingLabResultsButton = (
      <GenericButton
        key={appointment.id + "evaluating_lab_results"}
        disabled={allButtonsDisabled}
        className={styles.evaluationButton}
        data-testid="evaluating-lab-results-button"
        onClick={async () => {
          const requestData = {
            status: "evaluating_lab_results",
          };
          const isConfirmed = await showConfirmationModalAsync(
            t("appointment_confirm_status_change"),
          );

          if (!isConfirmed) return;

          api.genericApiRequest({
            entity: "appointment",
            method: "put",
            successHandler: async () => {
              await loadAppointment(appointmentId, "evaluating_lab_results");
            },
            failHandler: (error: any) => {
              handleSetError(error);
            },
            entityId: appointment.id,
            additionalRouteParts: additionalRouteParts,
            parametersToRender: {
              depth: "0",
            },
            submitData: requestData,
          });
        }}
      >
        {t("status_evaluating_lab_results")}
      </GenericButton>
    );

    const startReviewButton = (
      <GenericButton
        key={appointment.id + "open/in_progress"}
        color="secondary"
        disabled={statusButtonsDisabled}
        className={styles.button}
        data-testid="start-review-button"
        onClick={async () => {
          const requestData = {
            status: "review_preparation_required",
          };
          const isConfirmed = await showConfirmationModalAsync(
            t("appointment_confirm_review_status"),
          );

          if (!isConfirmed) return;

          api.genericApiRequest({
            entity: "appointment",
            method: "put",
            successHandler: async () => {
              await loadAppointment(
                appointmentId,
                "review_preparation_required",
              );
            },
            failHandler: (error: any) => {
              handleSetError(error);
            },
            entityId: appointment.id,
            additionalRouteParts: additionalRouteParts,
            parametersToRender: {
              depth: "0",
            },
            submitData: requestData,
          });
        }}
      >
        {t("dashboard_start_review")}
      </GenericButton>
    );
    const backToEditButton = (
      <GenericButton
        key={appointment.id + "waiting_for_review"}
        disabled={allButtonsDisabled}
        className={styles.button}
        onClick={async () => {
          const requestData = {
            status: "in_progress",
          };
          const isConfirmed = await showConfirmationModalAsync(
            t("appointment_confirm_back_to_progress"),
          );
          if (!isConfirmed) return;

          api.genericApiRequest({
            entity: "appointment",
            method: "put",
            successHandler: async () => {
              await loadAppointment(appointmentId);
            },
            failHandler: (error: any) => {
              handleSetError(error);
            },
            entityId: appointment.id,
            additionalRouteParts: additionalRouteParts,
            submitData: requestData,
          });
        }}
      >
        {t("dashboard_back_to_edit")}
      </GenericButton>
    );
    const createDocumentsButton = (
      <GenericButton
        color="secondary"
        className={styles.button}
        disabled={statusButtonsDisabled}
        key={appointment.id + "review_preparation_required"}
        onClick={async () => {
          const requestData = {
            status: "review_preparation_required",
          };
          const isConfirmed = await showConfirmationModalAsync(
            t("appointment_confirm_create_documents"),
          );
          if (!isConfirmed) return;
          api.genericApiRequest({
            entity: "appointment",
            method: "put",
            successHandler: async () => {
              await loadAppointment(
                appointmentId,
                "review_preparation_required",
              );
            },
            failHandler: async (error: any, status: any) => {
              if (status === 504) {
                await loadAppointment(
                  appointmentId,
                  "review_preparation_required",
                );
              } else {
                handleSetError(error);
              }
            },
            entityId: appointment.id,
            additionalRouteParts: additionalRouteParts,
            submitData: requestData,
          });
        }}
      >
        {t("dashboard_create_documents")}
      </GenericButton>
    );
    const finishAppointmentButton = (
      <GenericButton
        key={appointment.id + "post_processing_required"}
        className={styles.button}
        data-testid="finish-appointment-button"
        disabled={statusButtonsDisabled}
        color="error"
        onClick={async () => {
          if (!appointment?.subject?.is_registered) {
            showConfirmationModal(
              t("appointment_set_complete_error_user_not_registered"),
            );
            return;
          }
          const requestData = {
            status: "post_processing_required",
          };
          const isConfirmed = await showConfirmationModalAsync(
            t("appointment_confirm_finish_appointment"),
          );
          if (!isConfirmed) return;
          api.genericApiRequest({
            entity: "appointment",
            method: "put",
            successHandler: async () => {
              await loadAppointment(appointmentId, "post_processing_required");
            },
            failHandler: (error: any) => {
              handleError(error);
            },
            entityId: appointment.id,
            additionalRouteParts: additionalRouteParts,
            parametersToRender: {
              depth: "0",
            },
            submitData: requestData,
          });
        }}
      >
        {t("dashboard_finish_appointment")}
      </GenericButton>
    );

    const completedCardInfo = (
      <GenericButton
        key={"appointment_completed"}
        className={styles.cardButton}
        data-testid="appointment-completed-button"
        onClick={() => navigate("/appointmentDashboard")}
      >
        {t("appointment_completed")}
      </GenericButton>
    );
    if (appointment.status === "in_progress") {
      buttons.push(waitingForLabResultsButton);
    }
    if (appointment.status === "waiting_for_lab_results") {
      buttons.push(evaluatingLabResultsButton);
    }
    if (["open", "prefilled_by_user"].includes(appointment.status)) {
      buttons.push(inProgressButton);
    }
    if (
      [
        "in_progress",
        "waiting_for_lab_results",
        "evaluating_lab_results",
      ].includes(appointment.status)
    ) {
      buttons.push(
        withResponsibleUserTooltip(
          startReviewButton,
          appointment.id + "start_review_tooltip",
        ),
      );
    }

    if (
      appointment.status === "review_preparation_required" ||
      appointment.status === "waiting_for_review"
    ) {
      buttons.push(
        backToEditButton,
        withResponsibleUserTooltip(
          createDocumentsButton,
          appointment.id + "create_documents_tooltip",
        ),
        withResponsibleUserTooltip(
          finishAppointmentButton,
          appointment.id + "finish_appointment_tooltip",
        ),
      );
    }

    if (appointment.status === "post_processing_required") {
      buttons.push(
        backToEditButton,
        withResponsibleUserTooltip(
          finishAppointmentButton,
          appointment.id + "finish_appointment_tooltip",
        ),
      );
    }

    if (appointment.status === "completed") {
      buttons.push(completedCardInfo);
    }

    return buttons;
  };
  if (appointment) {
    appointmentActionsButtons = renderAppointmentActionButtons();
  }

  return (
    <>
      <Grid className={classNames(styles.buttonsWrapper, className)}>{appointmentActionsButtons}</Grid>
    </>
  );
};

export default AppointmentTreeItemActionButtons;
